import React, { useState } from "react";
import api from "../../../../api/api";
import QuestionnaireFill from "./QuestionnaireFill";
import { FormBadgeStrip } from "./QuestionnaireFill";
import { getPracticeId } from "../../../../config/practice";

// Standalone Forms station (sidebar → Forms, 2026-09-02): staff searches a
// patient by ID or name, picks questionnaires, and the flow launches straight
// into Patient View (fullscreen + PIN) — the tablet hand-off workflow without
// opening the chart. Mirrors the legacy cloud /bangquestions page.
const FormsStation = () => {
    const practiseid = getPracticeId();
    const [query, setQuery] = useState("");
    const [searching, setSearching] = useState(false);
    const [resultsList, setResultsList] = useState(null); // null = untouched
    const [patient, setPatient] = useState(null);
    const [fillOpen, setFillOpen] = useState(false);

    const search = async () => {
        const q = query.trim();
        if (!q || searching) return;
        setSearching(true);
        try {
            const res = await api.post(`/v1/${practiseid}/autocomplete/dssearch`, { searchtxt: q });
            const rows = Array.isArray(res?.data) ? res.data : [];
            // De-dup by patientid (dssearch can return one row per appointment).
            const seen = new Set();
            const patients = [];
            rows.forEach((r) => {
                if (!r?.patientid || seen.has(r.patientid)) return;
                seen.add(r.patientid);
                const full = (r.patientinfo || "").trim();
                const parts = full.split(/\s+/);
                patients.push({
                    patientid: r.patientid,
                    firstname: parts.length > 1 ? parts.slice(0, -1).join(" ") : full,
                    lastname: parts.length > 1 ? parts[parts.length - 1] : (r.lastname || ""),
                    fullname: full || `${r.lastname || ""}`,
                    dob: r.dob || "",
                });
            });
            setResultsList(patients);
        } catch (e) {
            setResultsList([]);
        } finally {
            setSearching(false);
        }
    };

    return (
        <div className="min-h-[calc(100vh-60px)] bg-[#F7F8FA] px-4 py-8">
            <div className="max-w-2xl mx-auto">
                <div className="mb-6">
                    <h1 className="text-[22px] font-bold text-[#111827] font-font1">Patient Questionnaires</h1>
                    <p className="text-[13px] text-[#6B7280] mt-1 font-font1">
                        Find the patient, pick the forms, set a PIN, and hand the tablet over.
                        The flow runs in Patient View — scores stay hidden until you unlock.
                    </p>
                </div>

                {/* Search */}
                <div className="bg-white rounded-2xl border border-[#E5E9F0] shadow-sm p-4 flex gap-3">
                    <input
                        autoFocus
                        className="flex-1 min-w-0 border border-[#E5E9F0] rounded-xl px-4 py-2.5 text-[15px] focus:border-[#1D2951] focus:outline-none"
                        placeholder="Patient ID or name…"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") search(); }}
                    />
                    <button
                        disabled={!query.trim() || searching}
                        onClick={search}
                        className={`px-6 py-2.5 rounded-xl text-[14px] font-semibold transition-all ${query.trim() && !searching
                            ? "bg-gradient-to-r from-[#E17954] to-[#d0623c] text-white shadow-md shadow-[#E17954]/25"
                            : "bg-gray-100 text-gray-400 cursor-not-allowed"}`}>
                        {searching ? "Searching…" : "Search"}
                    </button>
                </div>

                {/* Results */}
                {resultsList !== null && (
                    <div className="mt-4">
                        {resultsList.length === 0 ? (
                            <div className="text-center text-[14px] text-[#6B7280] py-10 font-font1">
                                No patients found — check the ID or spelling.
                            </div>
                        ) : resultsList.map((p) => (
                            <button key={p.patientid} type="button"
                                onClick={() => { setPatient(p); setFillOpen(true); }}
                                className="w-full bg-white rounded-2xl border border-[#E5E9F0] hover:border-[#1D2951] hover:shadow-md p-4 mb-3 flex items-center gap-4 text-left transition-all">
                                <span className="shrink-0 h-11 w-11 rounded-full bg-[#1D2951]/[0.07] text-[#1D2951] font-bold flex items-center justify-center text-[15px]">
                                    {(p.fullname || "?").split(/\s+/).map((w) => w[0]).join("").toUpperCase().slice(0, 2)}
                                </span>
                                <span className="min-w-0 flex-1">
                                    <span className="block text-[15px] font-semibold text-[#111827] font-font1 truncate">{p.fullname}</span>
                                    <span className="block text-[12px] text-[#8A93A6] font-font1">
                                        ID #{p.patientid}{p.dob ? `  ·  DOB ${p.dob}` : ""}
                                    </span>
                                </span>
                                <span className="shrink-0 text-[13px] font-semibold text-[#E17954]">Start forms →</span>
                            </button>
                        ))}
                    </div>
                )}

                {resultsList === null && <FormBadgeStrip />}
            </div>

            {patient && (
                <QuestionnaireFill
                    open={fillOpen}
                    data={patient}
                    forcePatientView
                    onClose={() => { setFillOpen(false); setPatient(null); }}
                />
            )}
        </div>
    );
};

export default FormsStation;
